// 관리자 API 클라이언트
const API_BASE_URL = import.meta.env.MODE === 'development'
  ? 'http://127.0.0.1:8788/api'
  : '/api';

// API 에러 처리
class AdminAPIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'AdminAPIError';
  }
}

// API 요청 헬퍼
async function adminRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('admin_token');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new AdminAPIError(response.status, error.error || 'Request failed');
  }

  return response.json();
}

// 인증 API
export const adminAuthAPI = {
  login: async (email: string, password: string) => {
    const data = await adminRequest<{ token: string; admin: any }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // 토큰 저장
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('admin_user', JSON.stringify(data.admin));

    return data;
  },

  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },

  getCurrentAdmin: async () => {
    return adminRequest<{ admin: any }>('/admin/auth/me');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('admin_token');
  },

  getStoredAdmin: () => {
    const adminStr = localStorage.getItem('admin_user');
    return adminStr ? JSON.parse(adminStr) : null;
  },
};

// 사용자 관리 API
export const adminUsersAPI = {
  getUsers: async (params?: { page?: number; limit?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);

    return adminRequest<{
      users: any[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/admin/users?${query}`);
  },

  getUser: async (id: number) => {
    return adminRequest<{ user: any; subscription: any; stats: any }>(`/admin/users/${id}`);
  },

  banUser: async (id: number, reason?: string) => {
    return adminRequest<{ success: boolean; message: string }>(`/admin/users/${id}/ban`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  },

  unbanUser: async (id: number) => {
    return adminRequest<{ success: boolean; message: string }>(`/admin/users/${id}/unban`, {
      method: 'PUT',
    });
  },

  deleteUser: async (id: number) => {
    return adminRequest<{ success: boolean; message: string }>(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  },

  changeSubscription: async (id: number, plan_type: 'free' | 'basic' | 'premium', expires_at?: string) => {
    return adminRequest<{ success: boolean; message: string; subscription: any }>(
      `/admin/users/${id}/subscription`,
      {
        method: 'PUT',
        body: JSON.stringify({ plan_type, expires_at }),
      }
    );
  },
};

// 강제 매칭 API
export const adminMatchesAPI = {
  createForcedMatch: async (data: {
    user_id: number;
    target_user_id: number;
    priority?: number;
    reason?: string;
    expires_at?: string;
  }) => {
    return adminRequest<{ success: boolean; forced_match_id: number; message: string }>(
      '/admin/matches/force',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  getForcedMatches: async (status: 'active' | 'completed' | 'cancelled' | 'all' = 'active') => {
    return adminRequest<{ forced_matches: any[] }>(`/admin/matches/force?status=${status}`);
  },

  getForcedMatch: async (id: number) => {
    return adminRequest<{ forced_match: any }>(`/admin/matches/forced/${id}`);
  },

  cancelForcedMatch: async (id: number) => {
    return adminRequest<{ success: boolean; message: string }>(`/admin/matches/forced/${id}`, {
      method: 'DELETE',
    });
  },
};

// 신고 관리 API
export const adminReportsAPI = {
  getReports: async (params?: { page?: number; limit?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.status) query.set('status', params.status);

    return adminRequest<{
      reports: any[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/admin/reports?${query}`);
  },

  resolveReport: async (id: number, data: { action: 'resolve' | 'dismiss'; resolution_note?: string }) => {
    return adminRequest<{ success: boolean; message: string }>(`/admin/reports/${id}/resolve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// 통계 API
export const adminStatsAPI = {
  getOverview: async () => {
    return adminRequest<{
      users: { total: number; new_today: number; online: number };
      matches: { total: number; today: number };
      messages: { total: number; today: number };
      subscriptions: { free: number; basic: number; premium: number };
      reports: { pending: number };
    }>('/admin/stats/overview');
  },
};

// 관리자 로그 API
export const adminAPI = {
  getLogs: async (params?: { limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    return adminRequest<{ logs: any[] }>(`/admin/logs?${query}`);
  },
};

export { AdminAPIError };
